// client/src/context/quizReducer.js - ENHANCED WITH TENANT SUPPORT
import { createContext, useReducer, useEffect } from 'react';

// Toggle persistence during development (set to false to disable)
const ENABLE_PERSISTENCE = false;
const STORAGE_KEY = 'mwc_quiz_state_v1';

// Get tenantId from URL
const getTenantIdFromUrl = () => {
  if (typeof window === 'undefined') return 'demo-university';
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('tenant') || 'demo-university';
};

// create context
export const QuizContext = createContext();

/* ---------- Helpers ---------- */
const safeParse = (json) => {
  try {
    return JSON.parse(json);
  } catch (err) {
    return null;
  }
};

const upsertResponse = (responses = [], newResp) => {
  if (!newResp || !newResp.questionId) return responses || [];
  const filtered = (responses || []).filter(r => r.questionId !== newResp.questionId);
  return [...filtered, newResp];
};

/* ---------- Reducer ---------- */
const quizReducer = (state, action) => {
  switch (action.type) {
    case 'SET_TENANT_ID':
      return { ...state, tenantId: action.payload || 'demo-university' };

    case 'SET_QUESTIONS':
      return { ...state, questions: Array.isArray(action.payload) ? action.payload : [] };

    case 'ADD_RESPONSE': {
      // payload: { questionId, selectedOptions: [] }
      const nextResponses = upsertResponse(state.responses, action.payload);
      return { ...state, responses: nextResponses };
    }

    case 'REMOVE_RESPONSE': {
      // payload: questionId
      return { ...state, responses: (state.responses || []).filter(r => r.questionId !== action.payload) };
    }

    case 'SET_CURRENT_INDEX':
      return { ...state, currentQuestionIndex: Number.isFinite(action.payload) ? action.payload : state.currentQuestionIndex };

    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload ?? null };

    case 'SET_RESULT_DATA': {
      const sessionDoc = action.payload?.session ?? action.payload ?? null;
      return {
        ...state,
        resultData: sessionDoc,
        programs: Array.isArray(sessionDoc?.programs) ? sessionDoc.programs : state.programs,
        resultContents: Array.isArray(sessionDoc?.resultContents) ? sessionDoc.resultContents : state.resultContents,
      };
    }

    case 'SET_PROGRAMS':
      return { ...state, programs: Array.isArray(action.payload) ? action.payload : [] };

    case 'SET_RESULT_CONTENTS':
      return { ...state, resultContents: Array.isArray(action.payload) ? action.payload : [] };

    case 'RESET_QUIZ':
      // Reset main quiz state but keep tenantId
      return {
        ...state,
        questions: [],
        responses: [],
        currentQuestionIndex: 0,
        sessionId: null,
        resultData: null,
        programs: [],
        resultContents: []
        // tenantId stays the same
      };

    case 'INIT_FROM_STORAGE':
      // payload expected to be a partial state loaded from localStorage
      return {
        ...state,
        ...action.payload,
        tenantId: getTenantIdFromUrl() // Always override with URL tenantId
      };

    default:
      return state;
  }
};

/* ---------- Provider (with optional persistence) ---------- */
const initialState = {
  tenantId: getTenantIdFromUrl(),
  questions: [],
  responses: [],
  currentQuestionIndex: 0,
  sessionId: null,
  resultData: null,
  programs: [],
  resultContents: []
};

function init(initial) {
  if (!ENABLE_PERSISTENCE) return initial;
  try {
    if (typeof window === 'undefined') return initial;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = safeParse(raw);
    if (!parsed) return initial;
    // Only pick known keys (defensive)
    return {
      ...initial,
      tenantId: getTenantIdFromUrl(), // Always get from URL, don't persist
      questions: Array.isArray(parsed.questions) ? parsed.questions : initial.questions,
      responses: Array.isArray(parsed.responses) ? parsed.responses : initial.responses,
      currentQuestionIndex: Number.isFinite(parsed.currentQuestionIndex) ? parsed.currentQuestionIndex : initial.currentQuestionIndex,
      sessionId: parsed.sessionId ?? initial.sessionId,
      resultData: parsed.resultData ?? initial.resultData,
      programs: Array.isArray(parsed.programs) ? parsed.programs : initial.programs,
      resultContents: Array.isArray(parsed.resultContents) ? parsed.resultContents : initial.resultContents
    };
  } catch (err) {
    return initial;
  }
}

export const QuizContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(quizReducer, initialState, init);

  // persist selected parts of state to localStorage
  useEffect(() => {
    if (!ENABLE_PERSISTENCE) return;
    try {
      const toSave = {
        // Don't save tenantId - always read from URL
        questions: state.questions,
        responses: state.responses,
        currentQuestionIndex: state.currentQuestionIndex,
        sessionId: state.sessionId,
        resultData: state.resultData,
        programs: state.programs,
        resultContents: state.resultContents
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (err) {
      // ignore storage errors silently in production/dev
    }
  }, [
    state.questions,
    state.responses,
    state.currentQuestionIndex,
    state.sessionId,
    state.resultData,
    state.programs,
    state.resultContents
  ]);

  return (
    <QuizContext.Provider value={{ ...state, state, dispatch }}>
      {children}
    </QuizContext.Provider>
  );
};